import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { BottomNav } from '../../components/feature/BottomNav';
import { WhatsAppFloat } from '../../components/feature/WhatsAppFloat';
import { Button } from '../../components/base/Button';
import { Card } from '../../components/base/Card';
import { SEOHead } from '../../components/seo/SEOHead';

export default function ReservarPage() {
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [extraRooms, setExtraRooms] = useState(0);
  const [extraBathrooms, setExtraBathrooms] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const services = [
    { id: 'regular', name: 'Regular Cleaning', price: 120, extraRoomPrice: 30, extraBathroomPrice: 30 },
    { id: 'profunda', name: 'Deep Cleaning', price: 180, extraRoomPrice: 35, extraBathroomPrice: 35 },
    { id: 'garaje', name: 'Garage Cleaning & Organization', price: 150 },
    { id: 'oficina', name: 'Office Cleaning', price: 150 },
    { id: 'ventanas', name: 'Window Cleaning', price: 35 },
    { id: 'post-construccion', name: 'Post-Construction', price: 200 },
    { id: 'comercial', name: 'Commercial Space', price: 150 },
    { id: 'mudanza', name: 'Move-in/Move-out Cleaning', price: 200 },
    { id: 'alfombras', name: 'Carpet Cleaning', price: 35 }
  ];

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00',
    '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedService || !selectedDate || !selectedTime || !formData.name || !formData.email || !formData.phone || !formData.address) {
      alert('Please complete all required fields');
      return;
    }

    // Preparar datos para envío
    const formDataToSend = new FormData();
    formDataToSend.append('service', selectedServiceData?.name || '');
    formDataToSend.append('base_price', selectedServiceData?.price.toString() || '');
    formDataToSend.append('extra_rooms', extraRooms.toString());
    formDataToSend.append('extra_bathrooms', extraBathrooms.toString());
    formDataToSend.append('total', calculateTotal().toString());
    formDataToSend.append('date', selectedDate);
    formDataToSend.append('time', selectedTime);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('notes', formData.notes);

    // Enviar al correo
    try {
      const response = await fetch('https://readdy.ai/api/form/d453hg7dee34hhmbecgg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        alert('Reservation sent successfully! We will contact you soon to confirm.');
        // Limpiar formulario
        setSelectedService('');
        setSelectedDate('');
        setSelectedTime('');
        setExtraRooms(0);
        setExtraBathrooms(0);
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          notes: ''
        });
      } else {
        alert('Error sending reservation. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error sending reservation. Please try again.');
    }
  };

  const selectedServiceData = services.find(s => s.id === selectedService);

  const calculateTotal = () => {
    if (!selectedServiceData) return 0;

    let total = selectedServiceData.price;

    if (selectedService === 'regular' || selectedService === 'profunda') {
      total += extraRooms * (selectedServiceData.extraRoomPrice || 0);
      total += extraBathrooms * (selectedServiceData.extraBathroomPrice || 0);
    }

    return total;
  };

  const showExtraOptions = selectedService === 'regular' || selectedService === 'profunda';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Book Cleaning Service - CleanPro Services"
        description="Book your professional cleaning service online. Choose from regular cleaning, deep cleaning, office cleaning, and more. Easy booking process with flexible scheduling."
        keywords="book cleaning service, schedule cleaning, cleaning appointment, professional cleaning booking"
      />
      
      <Header />

      <main className="pt-20 pb-20">
        {/* Header */}
        <section className="px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Book Service</h1>
          <p className="text-gray-600 text-sm">Complete the form to book your cleaning service</p>
        </section>

        <form onSubmit={handleSubmit} className="px-4 space-y-6" data-readdy-form>
          {/* Service Selection */}
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">1. Select your service</h3>
            <div className="space-y-3">
              {services.map((service) => (
                <label
                  key={service.id}
                  className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                    selectedService === service.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="service"
                      value={service.id}
                      checked={selectedService === service.id}
                      onChange={(e) => {
                        setSelectedService(e.target.value);
                        setExtraRooms(0);
                        setExtraBathrooms(0);
                      }}
                      className="opacity-0 absolute"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      selectedService === service.id ? 'border-blue-600' : 'border-gray-300'
                    }`}>
                      {selectedService === service.id && (
                        <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">{service.name}</span>
                      {(service.id === 'regular' || service.id === 'profunda') && (
                        <div className="text-sm text-green-600 font-semibold bg-green-100 px-2 py-1 rounded-md mt-1 inline-block">
                          Base price: 2 bedrooms, 1 bathroom
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-blue-600 font-bold">${service.price}</span>
                </label>
              ))}
            </div>
          </Card>

          {/* Extra Rooms and Bathrooms */}
          {showExtraOptions && (
            <Card>
              <h3 className="font-semibold text-gray-800 mb-4">Additional Rooms and Bathrooms</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Rooms
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => setExtraRooms(Math.max(0, extraRooms - 1))}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="ri-subtract-line text-gray-600"></i>
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-lg font-semibold">{extraRooms}</span>
                      <div className="text-xs text-gray-500">
                        ${selectedServiceData?.extraRoomPrice || 0} each
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setExtraRooms(extraRooms + 1)}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="ri-add-line text-gray-600"></i>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Bathrooms
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => setExtraBathrooms(Math.max(0, extraBathrooms - 1))}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="ri-subtract-line text-gray-600"></i>
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-lg font-semibold">{extraBathrooms}</span>
                      <div className="text-xs text-gray-500">
                        ${selectedServiceData?.extraBathroomPrice || 0} each
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setExtraBathrooms(extraBathrooms + 1)}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="ri-add-line text-gray-600"></i>
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Date Selection */}
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">2. Select date</h3>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </Card>

          {/* Time Selection */}
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">3. Select time</h3>
            <div className="grid grid-cols-3 gap-3">
              {timeSlots.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => setSelectedTime(time)}
                  className={`p-3 rounded-xl border-2 transition-colors ${
                    selectedTime === time
                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </Card>

          {/* Contact Information */}
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">4. Contact information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus-border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus-border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus-border-transparent resize-none"
                  placeholder="Special instructions, property access, etc."
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.notes.length}/500 characters
                </div>
              </div>
            </div>
          </Card>

          {/* Summary */}
          {selectedServiceData && (
            <Card className="bg-blue-50 border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-4">Reservation Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Service:</span>
                  <span className="font-medium">{selectedServiceData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Base price:</span>
                  <span className="font-medium">${selectedServiceData.price}</span>
                </div>
                {showExtraOptions && extraRooms > 0 && (
                  <div className="flex justify-between">
                    <span>Additional rooms ({extraRooms}):</span>
                    <span className="font-medium">${extraRooms * (selectedServiceData.extraRoomPrice || 0)}</span>
                  </div>
                )}
                {showExtraOptions && extraBathrooms > 0 && (
                  <div className="flex justify-between">
                    <span>Additional bathrooms ({extraBathrooms}):</span>
                    <span className="font-medium">${extraBathrooms * (selectedServiceData.extraBathroomPrice || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Date:</span>
                  <span className="font-medium">{selectedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span>Time:</span>
                  <span className="font-medium">{selectedTime}</span>
                </div>
                <div className="border-t border-blue-200 pt-2 mt-3">
                  <div className="flex justify-between text-lg font-bold text-blue-600">
                    <span>Total:</span>
                    <span>${calculateTotal()}</span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Submit Button */}
          <div className="pt-4 space-y-3">
            <Button type="submit" size="lg" fullWidth>
              <i className="ri-send-plane-line mr-2"></i>
              Send Reservation
            </Button>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-hand-coin-line text-green-600"></i>
                </div>
                <span className="text-green-700 font-semibold">Pay when service is complete</span>
              </div>
              <p className="text-green-600 text-sm">
                No need to pay now. Payment is made once the service is completed to your satisfaction.
              </p>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-2">
              Your reservation will be sent to our team for confirmation.
            </p>
          </div>
        </form>
      </main>

      <BottomNav />
      <WhatsAppFloat />
    </div>
  );
}
